import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, UserPlus } from 'lucide-react';
import { useRegister } from '@/hooks/useApi';

interface FieldProps {
  label: string;
  field: string;
  type?: string;
  placeholder?: string;
  form: Record<string, string>;
  errors: Record<string, string>;
  onChange: (field: string, value: string) => void;
}

const Field = ({ label, field, type = 'text', placeholder, form, errors, onChange }: FieldProps) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">{label}</label>
    <input
      type={type}
      value={form[field] ?? ''}
      onChange={(e) => onChange(field, e.target.value)}
      placeholder={placeholder}
      className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-brand-400 transition-colors text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800 placeholder-gray-400 dark:placeholder-zinc-500 ${
        errors[field] ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-zinc-700'
      }`}
    />
    {errors[field] && <p className="text-red-500 text-xs mt-1">{errors[field]}</p>}
  </div>
);

export default function RegisterPage() {
  const navigate = useNavigate();
  const { mutate: register, isLoading } = useRegister();

  const [form, setForm] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const getAgeFromDob = (dob: string) => {
    if (!dob) return null;
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const update = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Invalid email address';
    if (!form.username) newErrors.username = 'Username is required';
    else if (!/^[A-Za-z0-9_]{3,50}$/.test(form.username)) newErrors.username = 'Username must be 3-50 characters, letters/numbers/underscores only';
    if (!form.password) newErrors.password = 'Password is required';
    else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(form.password)) newErrors.password = 'Password must be at least 8 characters with uppercase, lowercase, and a number';
    if (!form.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (form.password !== form.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (!form.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required';
    } else {
      const age = getAgeFromDob(form.date_of_birth);
      if (age === null || age < 13) newErrors.date_of_birth = 'You must be at least 13 years old to join';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    register(
      {
        email: form.email,
        username: form.username,
        password: form.password,
        first_name: form.first_name || undefined,
        last_name: form.last_name || undefined,
        date_of_birth: form.date_of_birth,
      },
      { onSuccess: () => navigate('/dashboard') }
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 dark:from-brand-900/40 to-accent-50 dark:to-zinc-950 px-4 py-12">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-gray-200 dark:border-zinc-800 p-8 w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-brand-500 to-accent-500 text-white text-2xl mb-4 shadow-lg dark:shadow-black/40">
            🃏
          </div>
          <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-zinc-100">Create your account</h1>
          <p className="text-gray-500 dark:text-zinc-500 text-sm mt-1">Join thousands of collectors today</p>
        </div>

        <div className="space-y-4">
          {/* Name row */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="First Name" field="first_name" placeholder="John" form={form} errors={errors} onChange={update} />
            <Field label="Last Name" field="last_name" placeholder="Doe" form={form} errors={errors} onChange={update} />
          </div>

          <Field label="Username" field="username" placeholder="coolcollector99" form={form} errors={errors} onChange={update} />
          <Field label="Email" field="email" type="email" placeholder="you@example.com" form={form} errors={errors} onChange={update} />

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => update('password', e.target.value)}
                placeholder="At least 8 characters"
                className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-brand-400 transition-colors pr-11 text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800 placeholder-gray-400 dark:placeholder-zinc-500 ${
                  errors.password ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-zinc-700'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
          </div>

          <Field label="Confirm Password" field="confirmPassword" type="password" placeholder="••••••••" form={form} errors={errors} onChange={update} />

          {/* Date of birth */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">
              Date of Birth
            </label>
            <input
              type="date"
              value={form.date_of_birth}
              onChange={(e) => {
                update('date_of_birth', e.target.value);
              }}
              max={new Date().toISOString().split('T')[0]}
              className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-brand-400 transition-colors text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800 ${
                errors.date_of_birth ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-zinc-700'
              }`}
            />
            {errors.date_of_birth && <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>}
            {(() => {
              const age = getAgeFromDob(form.date_of_birth);
              if (age !== null && age >= 13 && age < 18) {
                return (
                  <p className="text-amber-600 dark:text-amber-400 text-xs mt-1">
                    Users under 18 can browse, collect, and trade — but cannot buy, sell, or bid with real money.
                  </p>
                );
              }
              return null;
            })()}
            <p className="text-xs text-gray-500 dark:text-zinc-500 mt-1">
              By creating an account you agree to the{' '}
              <Link to="/legal/terms" target="_blank" className="text-brand-600 dark:text-brand-400 hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/legal/privacy" target="_blank" className="text-brand-600 dark:text-brand-400 hover:underline">Privacy Policy</Link>.
            </p>
          </div>

          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <UserPlus className="h-4 w-4" />
            )}
            {isLoading ? 'Creating account...' : 'Create Account'}
          </button>
        </div>

        <p className="text-center text-sm text-gray-500 dark:text-zinc-500 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-600 dark:text-brand-400 font-medium hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}